import type { Node, RenderableTreeNode } from "@markdoc/markdoc";

import {
  DEFAULT_LANGUAGE,
  SUPPORTED_LANGUAGES,
  type SupportedLanguage,
} from "@mountayaapp/locales";
import { type Frontmatter, parseDocument, parseMarkdoc, type TocEntry } from "@mountayaapp/markdoc";

import { ELEMENT_DOCS } from "../elements.gen";
import { buildTabSource, isPageTabSlug, listDerivedTabs, resolveDerivedTab } from "./elementTabs";
import { buildDocHref } from "./href";
import {
  type NavGroup as CuratedNavGroup,
  groupSlugs,
  NAVIGATION,
  resolveTabId,
  resolveTabItem,
  TABS,
  type Tab,
  type TabId,
  type TabItem,
} from "./navigation";

export type { Tab, TabId, TabItem };
// Re-export the tab information architecture from the registry so the header,
// shell, and App import the tab config and types from the same content surface
// they already read the manifest from, rather than reaching into the navigation
// module directly. The navigation module stays the place the tabs are defined.
export { resolveTabItem, TABS };

/**
 * @mountayaapp/docs content registry. Authored Markdoc pages live under
 * `content/<lang>/**` and are pulled in at build time with `import.meta.glob`,
 * parsed once, and indexed by language and slug. The module is render-free (no
 * JSX) so both the client entry and the prerender step import it; the JSX
 * renderers consume the parsed `tree` separately. The sidebar order is the
 * curated tree in `./navigation`, not the pages' frontmatter — frontmatter owns
 * only each page's `title` and `description`.
 */

/**
 * DocEntry is one authored page: its language, its trailing-slash-free slug
 * (empty for a language root), the validated frontmatter, the renderable tree,
 * the table of contents, and the canonical href the page is served at.
 */
export interface DocEntry {
  frontmatter: Frontmatter;
  href: string;
  lang: SupportedLanguage;
  slug: string;
  toc: TocEntry[];
  tree: RenderableTreeNode;
}

/**
 * NavItem is one link in the sidebar navigation: a page's localized title and
 * the href it points to.
 */
export interface NavItem {
  href: string;
  title: string;
}

/**
 * NavSection is one resolved nested section of a group: the localized `labelKey` heading it
 * is announced by, and the pages under it.
 *
 * The heading is never a page. It used to be able to be one, for a rail organized by npm
 * package; a task-shaped rail groups peers under a job, and no page documents the job on
 * its own. So the heading is a disclosure BUTTON rather than a link, which is also the
 * reason `DocsNavbar` never marks one `aria-current="page"`.
 */
export interface NavSection {
  items: NavItem[];
  labelKey: string;
}

/** One entry in a resolved group: a page, or a section of pages. `"items" in node` tells them apart. */
export type NavNode = NavItem | NavSection;

/**
 * NavGroup is one resolved section of the sidebar navigation: the owning `tab`,
 * the localized `labelKey` and Nucleo `icon` from the curated tree, plus the pages
 * resolved from that group's entries for the active language.
 *
 * The pages arrive twice, from ONE pass: `tree` is the shape the rail draws, nested where the
 * curated tree nests, and `items` is that same list flattened into reading order for the pager,
 * the breadcrumbs and the search index. Deriving both here is what keeps a nested page in the
 * reading order rather than making it the one the pager steps over.
 *
 * This module is render-free, so it carries the `labelKey` and the `icon` component rather than
 * a rendered heading; the consumer translates the key and draws the icon. The `tab` tag lets
 * `getNavManifest` hand a sidebar only the groups its active tab owns.
 */
export interface NavGroup {
  icon: CuratedNavGroup["icon"];
  /** The dropdown destination this group belongs to, carried from the curated tree. */
  item?: string;
  items: NavItem[];
  labelKey: string;
  tab: TabId;
  tree: NavNode[];
}

interface RawDoc {
  frontmatter: Frontmatter;
  lang: SupportedLanguage;
  slug: string;
  toc: TocEntry[];
  tree: RenderableTreeNode;
}

/**
 * isSupportedLanguage narrows a path-derived language segment to a supported
 * language, so content under an unrecognized directory is ignored rather than
 * indexed under an invalid key.
 */
const isSupportedLanguage = (value: string): value is SupportedLanguage =>
  (SUPPORTED_LANGUAGES as readonly string[]).includes(value);

/**
 * deriveLocation turns a globbed file path into its `(lang, slug)`. It strips
 * everything up to and including the `content/` segment and the `.md` suffix;
 * the first remaining segment is the language and the rest is the slug. A
 * trailing `index` segment collapses to its parent path, so each section is a
 * folder with an `index.md` landing: `map-publishing/index.md` is the slug
 * `map-publishing` and a top-level `index.md` is the empty slug (the language
 * root). A flat file keeps its path as the slug.
 */
export const deriveLocation = (path: string): { lang: string; slug: string } => {
  const afterContent = path.replace(/^.*\/content\//, "").replace(/\.md$/, "");
  const [lang = "", ...rest] = afterContent.split("/");
  const joined = rest.join("/");
  const slug = joined === "index" ? "" : joined.replace(/\/index$/, "");

  return { lang, slug };
};

/**
 * partialModules are the raw source of every shared partial under
 * `content/_partials/**`. Pages reference them with `{% partial file="…" %}`,
 * where the `file` value is the partial's path relative to the `content/`
 * directory — for example `_partials/activities.md`. Keeping the enum and
 * parameter tables in partials lets a page include them without duplicating the
 * markup, and a single `file=` convention means the same string identifies a
 * partial whether it is included from a top-level page or a nested section.
 */
const partialModules = import.meta.glob<string>("../../../content/_partials/**/*.md", {
  eager: true,
  import: "default",
  query: "?raw",
});

/**
 * partials maps each `{% partial file="…" %}` key to its parsed Markdoc AST. The
 * key is the partial's path relative to `content/` (e.g. `_partials/activities.md`),
 * matching the `file` attribute authors write. `parseDocument` merges this map
 * into the transform config so a page's partial reference expands to that AST at
 * parse time.
 */
const partials: Record<string, Node> = {};
for (const [path, source] of Object.entries(partialModules)) {
  const key = path.replace(/^.*\/content\//, "");
  // The package's own door, not the bare parser: a partial parsed any other way renders its
  // HTML comments while the page including it does not.
  partials[key] = parseMarkdoc(source);
}

const rawModules = import.meta.glob<string>("../../../content/**/*.md", {
  eager: true,
  import: "default",
  query: "?raw",
});

/**
 * rawDocs parses every authored page once into its frontmatter, tree, and ToC,
 * dropping any file under an unsupported language directory and any partial
 * (partials are includes, not pages, and are merged into the parse config rather
 * than indexed as routes). Each page is parsed with the shared `partials` so a
 * `{% partial file="…" %}` reference expands inline.
 */
const rawDocs: RawDoc[] = Object.entries(rawModules).flatMap(([path, source]) => {
  const { lang, slug } = deriveLocation(path);
  if (!isSupportedLanguage(lang)) {
    return [];
  }

  try {
    const { frontmatter, toc, tree } = parseDocument(source, { elements: ELEMENT_DOCS, partials });

    return [{ frontmatter, lang, slug, toc, tree }];
  } catch (error) {
    // A single unparseable page (typically malformed YAML frontmatter) must not
    // take down the whole site, so drop it with a dev warning instead of throwing
    // through this module's top-level evaluation.
    if (import.meta.env.DEV) {
      console.warn(`@mountayaapp/docs: skipping unparseable page ${path}`, error);
    }

    return [];
  }
});

/**
 * docsByLanguage indexes parsed pages by language then slug for O(1) lookup by
 * the resolver, attaching each page's canonical href.
 */
const docsByLanguage = new Map<SupportedLanguage, Map<string, DocEntry>>();
for (const raw of rawDocs) {
  const bySlug = docsByLanguage.get(raw.lang) ?? new Map<string, DocEntry>();
  bySlug.set(raw.slug, { ...raw, href: buildDocHref(raw.lang, raw.slug) });
  docsByLanguage.set(raw.lang, bySlug);
}

/**
 * isReachableSlug answers whether a slug is reachable from the chrome: a sidebar group
 * lists it, a non-fallback header tab owns it, or a page's own tab strip carries it.
 *
 * Three kinds of page are filed in no rail on purpose. The full-width explorers hang off
 * the API references tab, because each carries its own navigation and a rail entry would
 * offer a link that leaves the rail behind. A page-level tab — a Props or Styling table,
 * the hosted embed's parameter reference — hangs off the guide it belongs to, and keeping
 * it out of `NAVIGATION` is what keeps it out of the pager's reading order and the
 * breadcrumb trail at the same time. Both clauses exist so neither reports as an orphan
 * below.
 */
const curatedSlugs = new Set<string>(NAVIGATION.flatMap(groupSlugs));
const isReachableSlug = (slug: string): boolean =>
  curatedSlugs.has(slug) || resolveTabId(slug).id !== "documentation" || isPageTabSlug(slug);

/**
 * tabDocs memoizes the synthesized tab pages, keyed by `lang:slug`. A `null` records a
 * slug already resolved to nothing, so a miss is answered once rather than re-derived on
 * every lookup.
 */
const tabDocs = new Map<string, DocEntry | null>();

/**
 * synthesizeTabDoc builds one derived tab page: the Markdoc source is computed from the
 * element catalogue and parsed exactly as an authored page is, so it carries the same
 * frontmatter, tree and table of contents and every consumer downstream treats it as a
 * page rather than as a special case.
 *
 * Built on demand and memoized, never eagerly. The authored corpus is already parsed at
 * this module's init — the largest single cost this app pays in the browser — so parsing
 * the whole derived set there too is exactly the cost not to add. A reader asking for one
 * tab pays for that tab.
 */
const synthesizeTabDoc = (lang: SupportedLanguage, slug: string): DocEntry | null => {
  const derived = resolveDerivedTab(slug);
  if (derived === null) {
    return null;
  }

  const guide =
    docsByLanguage.get(lang)?.get(derived.guideSlug) ??
    docsByLanguage.get(DEFAULT_LANGUAGE)?.get(derived.guideSlug);
  if (guide === undefined) {
    return null;
  }

  const source = buildTabSource(
    derived,
    guide.frontmatter.title,
    buildDocHref(lang, derived.guideSlug),
  );

  try {
    const { frontmatter, toc, tree } = parseDocument(source, { elements: ELEMENT_DOCS, partials });

    return { frontmatter, href: buildDocHref(lang, slug), lang, slug, toc, tree };
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn(`@mountayaapp/docs: skipping unbuildable tab page ${slug}`, error);
    }

    return null;
  }
};

/**
 * findTabDoc resolves a derived tab page through the memo, building it on the first ask.
 */
const findTabDoc = (lang: SupportedLanguage, slug: string): DocEntry | null => {
  const key = `${lang}:${slug}`;
  const cached = tabDocs.get(key);
  if (cached !== undefined) {
    return cached;
  }

  const built = synthesizeTabDoc(lang, slug);
  tabDocs.set(key, built);

  return built;
};

/**
 * findDoc looks up a page by language and slug, returning `null` when no page is
 * registered at that location. An authored page answers first; a slug that names a
 * guide's derived tab is built from the element catalogue on the spot.
 */
export const findDoc = (lang: SupportedLanguage, slug: string): DocEntry | null =>
  docsByLanguage.get(lang)?.get(slug) ?? findTabDoc(lang, slug);

/**
 * resolveManifest turns the curated navigation tree into the sidebar for one
 * language. Each group keeps its `labelKey` and `icon`; each slug resolves to a
 * page in the requested language, falling back to the default language so a
 * not-yet-translated page still links to its English version. A slug with no
 * page in either language is omitted, so the rendered sidebar never carries a
 * dead link while content is still being authored.
 */
const resolveManifest = (lang: SupportedLanguage): NavGroup[] =>
  NAVIGATION.map(({ entries, icon, item: ownerItem, labelKey, tab }) => {
    const resolve = (slug: string): NavItem | null => {
      const entry = findDoc(lang, slug) ?? findDoc(DEFAULT_LANGUAGE, slug);

      return entry ? { href: buildDocHref(lang, slug), title: entry.frontmatter.title } : null;
    };

    const items: NavItem[] = [];
    const tree: NavNode[] = [];

    for (const entry of entries) {
      if (typeof entry === "string") {
        const item = resolve(entry);
        if (item) {
          items.push(item);
          tree.push(item);
        }

        continue;
      }

      const nested = entry.slugs.flatMap((slug) => resolve(slug) ?? []);

      // A section whose pages all resolve to nothing is a section declared ahead of its
      // content, and its heading goes with them — a heading over an empty disclosure is a
      // reader's dead end even when every link under it was correctly omitted.
      if (nested.length === 0) {
        continue;
      }

      items.push(...nested);
      tree.push({ items: nested, labelKey: entry.labelKey });
    }

    return {
      icon,
      ...(ownerItem === undefined ? {} : { item: ownerItem }),
      items,
      labelKey,
      tab,
      tree,
    };
  });

const manifestByLanguage = new Map<SupportedLanguage, NavGroup[]>();
for (const lang of docsByLanguage.keys()) {
  manifestByLanguage.set(lang, resolveManifest(lang));
}

/**
 * warnNavigationGaps reports two kinds of authoring drift, only while developing
 * (so a production build is never noisy): a curated slug that resolves to no
 * page in any language (a sidebar entry that would be dead, and is omitted), and
 * an authored page that no group references (an orphan unreachable from the
 * sidebar). Both are warnings, never throws, so the app builds before every
 * page in the tree exists.
 *
 * A group whose slugs ALL resolve to nothing is a section declared ahead of its
 * content rather than drift, so it is skipped: the chrome already omits an empty
 * group and hides its tab. A group where only SOME slugs resolve is real drift and
 * still warns.
 */
const warnNavigationGaps = (): void => {
  if (!import.meta.env.DEV) {
    return;
  }

  const resolvableSlugs = new Set<string>();
  for (const bySlug of docsByLanguage.values()) {
    for (const slug of bySlug.keys()) {
      resolvableSlugs.add(slug);
    }
  }

  for (const group of NAVIGATION) {
    const slugs = groupSlugs(group);
    if (!slugs.some((slug) => resolvableSlugs.has(slug))) {
      continue;
    }

    for (const slug of slugs) {
      if (!resolvableSlugs.has(slug)) {
        console.warn(
          `[docs] navigation references "${slug}" (group "${group.labelKey}") but no page resolves it; the sidebar entry is omitted.`,
        );
      }
    }
  }

  for (const slug of resolvableSlugs) {
    if (!isReachableSlug(slug)) {
      console.warn(
        `[docs] page "${slug}" is authored but no sidebar group lists it and no header tab owns it; it is unreachable from the chrome.`,
      );
    }
  }
};

warnNavigationGaps();

/**
 * getNavManifest returns the curated sidebar navigation for a language, optionally
 * narrowed to a single tab and, within a dropdown tab, to a single destination. With no
 * `tabId` it returns every group (used by the breadcrumbs, the pager, and the discovery
 * files, which resolve a page's owning section across the whole tree); with a `tabId` it
 * returns only that tab's groups; with an `itemId` as well it returns only the groups
 * that destination owns — the SDKs rail shows ONE language's packages, not every
 * language's at once. Groups that resolved to no items are dropped, so a section declared
 * ahead of its content never renders as an empty heading. A language with no content yet
 * yields an empty list.
 */
export const getNavManifest = (
  lang: SupportedLanguage,
  tabId?: TabId,
  itemId?: string,
): NavGroup[] => {
  const groups = (manifestByLanguage.get(lang) ?? []).filter((group) => group.items.length > 0);
  const scoped = tabId ? groups.filter((group) => group.tab === tabId) : groups;

  return itemId === undefined ? scoped : scoped.filter((group) => group.item === itemId);
};

/**
 * resolvesInLanguage answers whether a slug has a page in a language, falling back to the
 * default the way the manifest does, so a not-yet-translated section still shows.
 */
const resolvesInLanguage = (lang: SupportedLanguage, slug: string): boolean =>
  findDoc(lang, slug) !== null || findDoc(DEFAULT_LANGUAGE, slug) !== null;

/**
 * listVisibleTabs returns the header tabs that have somewhere to go in a language.
 *
 * A tab that NAVIGATES renders only when its landing page resolves: the tab IS a link to
 * that page, so one whose landing slug has no page would be a dead link. A tab that OPENS
 * A MENU renders when any of its destinations resolves, and a `planned` item never counts
 * — it has no slug to point at, so a tab of nothing but plans would be a menu of dead
 * ends. This is the single seam a section declared ahead of its content passes through,
 * so authoring its pages is all it takes to turn the tab on.
 */
export const listVisibleTabs = (lang: SupportedLanguage): Tab[] =>
  TABS.filter((tab) =>
    "items" in tab
      ? tab.items.some((item) => "slug" in item && resolvesInLanguage(lang, item.slug))
      : resolvesInLanguage(lang, tab.landingSlug),
  );

/**
 * resolveTab maps a URL pathname to the header tab that owns it. The first path
 * segment is the language prefix and the rest is the slug; that slug runs through
 * `resolveTabId`, so the same ownership rule drives the header's active-state, the
 * shell's layout choice, and the sidebar's tab filter from one place. A pathname
 * with no slug (a language root) resolves to the Documentation fallback.
 */
export const resolveTab = (pathname: string): Tab => {
  const segments = pathname.split("/").filter(Boolean);
  const slug = segments.slice(1).join("/");

  return resolveTabId(slug);
};

/**
 * listLanguages returns the languages that have at least one authored page, in
 * the order the supported-language list declares them.
 */
export const listLanguages = (): SupportedLanguage[] =>
  SUPPORTED_LANGUAGES.filter((lang) => docsByLanguage.has(lang));

/**
 * listEntries returns every page across all languages — the authored corpus plus every
 * derived tab page — used by the prerender step to emit one static file per page.
 *
 * This is the one caller that forces the whole derived set, and it does so deliberately:
 * the build has to emit each tab page, and the search index has to find the props that
 * left the guide bodies. Everything else resolves one page at a time through `findDoc`.
 */
export const listEntries = (): DocEntry[] => [
  ...[...docsByLanguage.values()].flatMap((bySlug) => [...bySlug.values()]),
  ...[...docsByLanguage.keys()].flatMap((lang) =>
    listDerivedTabs().flatMap(
      ({ guideSlug, tab }) => findTabDoc(lang, `${guideSlug}/${tab}`) ?? [],
    ),
  ),
];
